import React, { createContext, useContext, useMemo, useState, useEffect } from "react";

const translations = {
  en: {
    roles: {
      admin: "Admin",
      cashier: "Cashier",
    },
    language: {
      english: "English",
      turkish: "Turkish",
      label: "Language",
    },
    navigation: {
      brand: "METU CAFETERIA",
      home: "HOME",
      admin: "ADMIN",
      service: "CARD READER",
      logout: "LOGOUT",
      welcome: "Welcome, {name} ({role})",
      user: "User",
    },
    home: {
      title: "METU Cafeteria Management System",
      subtitle:
        "Modern prepaid balance system for cafeteria management with unlimited meal access based on available credits",
      adminPanelTitle: "Admin Panel",
      adminPanelDescription: "Manage student accounts, add balance, and monitor system usage",
      adminPanelCta: "Open Admin Panel",
      serviceTitle: "Card Reader Interface",
      serviceDescription:
        "Quick card scanning interface for cafeteria staff to process meal payments",
      serviceCta: "Open Card Reader",
      featuresTitle: "New System Features",
      features: {
        prepaidTitle: "Prepaid Balance System",
        prepaidDescription: "Students load credits to their cards and spend as needed",
        flexibleTitle: "Flexible Meal Options",
        flexibleDescription: "Table d'hôte (100 TRY), Alacarte (150 TRY), or custom pricing",
        unlimitedTitle: "Unlimited Usage",
        unlimitedDescription: "No daily limits - eat as much as your balance allows",
      },
    },
    auth: {
      loginTitle: "METU Cafeteria Management",
      loginSubtitle: "Admin Login Required",
      username: "Username",
      password: "Password",
      usernamePlaceholder: "Enter your username",
      passwordPlaceholder: "Enter your password",
      signIn: "Sign In",
      signingIn: "Signing in...",
      defaultCredentials: "Default Credentials:",
      defaultUsername: "Username",
      defaultPassword: "Password",
      defaultWarning: "Please change the default password after first login.",
      errors: {
        requiredFields: "Please fill in all fields",
        loginFailed: "Login failed. Please check your credentials.",
        welcomeBack: "Welcome back, {username}!",
      },
    },
    adminPanel: {
      title: "Admin Panel",
      description: "Manage student accounts and prepaid balances",
      loading: "Loading students...",
      addStudent: "Add New Student",
      editStudent: "Edit Student",
      addAdmin: "Add Admin/Cashier",
      cleanup: "Cleanup Expired (100+ days)",
      studentsCard: {
        title: "Student Management",
        subtitle: "Create, update, and manage student subscriptions",
      },
      status: {
        active: "Active",
        expired: "Expired",
        inactive: "Inactive",
        expiringSoon: "Expires Soon",
        noBalance: "No Balance",
      },
      mealTypes: {
        table_dhote: "Table d'hôte",
        alacarte: "Alacarte",
        custom: "Custom",
        unknown: "Unknown",
      },
      stats: {
        totalStudents: "Total Students",
        activeStudents: "Active",
        expiringSoon: "Expiring Soon",
        expired: "Expired",
        transactionsToday: "Trans. Today",
        totalBalance: "Total Balance",
        revenue: "Revenue",
      },
      form: {
        name: "Name",
        surname: "Surname",
        schoolId: "School ID",
        cardId: "Card ID",
        cardIdReadOnly: "Card ID (Read-only)",
        mealType: "Meal Type",
        duration: "Duration",
        mealsPerDay: "Meals Per Day",
        customBalance: "Custom Balance (TRY)",
        calculate: "Balance Calculation",
        submit: "Add Student",
        update: "Update Student",
        cancel: "Cancel",
        scanPrompt: "Step 1: Scan Card ID *",
        scanning: "Checking...",
        lookup: "Check Card",
        lookupPlaceholder: "Scan card or enter ID (Press Enter)",
        scanHelp: "Scan the student's card or press Enter after typing to auto-fill details.",
        balance: "Balance (TRY)",
      },
      placeholders: {
        name: "Enter student name",
        surname: "Enter student surname",
        schoolId: "Enter school ID",
        customBalance: "Enter custom balance amount",
      },
      mealOptions: {
        tableDhote: "Table d'hôte (3000 TRY base)",
        alacarte: "Alacarte (4500 TRY base)",
        custom: "Custom Amount",
      },
      durationOptions: {
        30: "30 Days",
        60: "60 Days",
        90: "90 Days",
      },
      mealsPerDayOptions: {
        one: "1 Meal Per Day",
        two: "2 Meals Per Day",
      },
      balance: {
        custom: "Custom Balance: {amount}",
        base: "Base Price:",
        duration: "Duration:",
        days: "days",
        mealsPerDay: "Meals/Day:",
        total: "Total Balance: {amount}",
      },
      table: {
        name: "Name",
        cardId: "Card ID",
        schoolId: "School ID",
        balance: "Balance",
        expires: "Expire Date",
        status: "Status",
        actions: "Actions",
        noStudents: "No students found",
        emptyHelper: "Add your first student to get started",
      },
      notifications: {
        fetchStudentsError: "Failed to fetch students",
        newCard: "✨ New card detected - please fill student information",
        cardActive:
          "⚠️ Card already has ACTIVE subscription!\nStudent: {name}\nCurrent balance: {balance}\nExpires: {expires}\nUse Edit function to modify active subscriptions.",
        cardExpired:
          "🔄 Card found with EXPIRED subscription.\nStudent: {name}\nPrevious balance: {balance} (will be zeroed)\nExpired: {expires}\nReady to create new subscription!",
        cardInactive:
          "📋 Card found with inactive subscription.\nStudent: {name}\nPrevious balance: {balance} (will be zeroed)\nReady to create new subscription!",
        lookupError: "Failed to lookup card",
        addSuccess: "Student {name} {surname} added successfully! Balance: {balance}",
        addError: "Please fill in all required fields",
        addFailed: "Failed to add student",
        updateSuccess: "Student updated successfully! New balance: {balance}",
        updateError: "Failed to update student",
        deleteSuccess: "Student {name} deleted successfully!",
        deleteError: "Failed to delete student",
        adminAddSuccess: "Admin {username} created successfully!",
        adminAddError: "Please fill in admin username and password",
        adminAddFailed: "Failed to add admin",
        cleanupSuccess: "Expired subscriptions cleaned successfully",
        cleanupError: "Failed to cleanup expired subscriptions",
      },
      deleteConfirm: {
        title: "Delete Student",
        description: "Are you sure you want to delete {name}? This action cannot be undone.",
        confirm: "Delete",
      },
      adminForm: {
        title: "Create Admin or Cashier",
        username: "Username",
        usernamePlaceholder: "Enter username",
        password: "Password",
        passwordPlaceholder: "Enter password",
        role: "Role",
        submit: "Add User",
      },
      roles: {
        admin: "Admin (Full Access)",
        cashier: "Cashier (Card Reader Only)",
        help: "Admin: Full access to all features. Cashier: Card reader access only.",
      },
      editHelp: "You can adjust the student's balance here. Perfect for adding funds or correcting balances.",
    },
    service: {
      title: "Card Reader Interface",
      subtitle: "Scan student card to check balance and process meal payments",
      lastScanned: "Last scanned: {time}",
      cardScannerLabel: "Card ID Scanner",
      cardPlaceholder: "Scan card or type card ID...",
      checkCard: "Check Card",
      checking: "Checking...",
      clear: "Clear",
      cardFound: "Card found: {student}",
      systemError: "System error - please try again",
      deductionError: "Failed to deduct meal",
      scanPrompt: "Please scan a valid card first",
      customAmountPrompt: "Please enter a valid custom amount",
      cardStatus: {
        found: "CARD FOUND",
        issue: "CARD ISSUE",
        message: "{message}",
      },
      balance: "Balance: {amount}",
      expires: "Expires: {date}",
      expiresSoon: "Expires Soon",
      selectMeal: "Select Meal Type",
      meals: {
        table_dhote: "Table d'hôte",
        alacarte: "Alacarte",
        custom: "Custom Amount",
        customLabel: "₺ Custom",
      },
      customPlaceholder: "Enter custom amount (TRY)",
      processing: "Processing...",
      deduct: "Deduct {amount}",
      paymentResult: "Payment Result",
      paymentSuccessful: "PAYMENT SUCCESSFUL",
      paymentFailed: "PAYMENT FAILED",
      nextCustomer: "Next Customer",
      tryAgain: "Try Again",
      paymentDetails: {
        student: "Student:",
        amountDeducted: "Amount Deducted:",
        previousBalance: "Previous Balance:",
        newBalance: "New Balance:",
      },
      instructionsTitle: "Instructions",
      instructions: [
        "• Scan the student's RFID card or manually enter the card ID",
        "• The system will check the student's balance and subscription status",
        "• Select meal type: Table d'hôte (100 TRY), Alacarte (150 TRY), or custom amount",
        "• Click \"Deduct\" to process the meal payment",
        "• No daily limits - students can eat as long as they have balance",
        "• Green = Payment successful, Red = Payment failed or insufficient balance",
        "• Subscriptions expire automatically after 100+ days of inactivity",
      ],
      quickStats: {
        success: "Payment Success",
        failed: "Payment Failed",
        table: "Table d'hôte",
        alacarte: "Alacarte",
        unlimited: "No Daily Limits",
      },
    },
  },
  tr: {
    roles: {
      admin: "Yönetici",
      cashier: "Kasiyer",
    },
    language: {
      english: "İngilizce",
      turkish: "Türkçe",
      label: "Dil",
    },
    navigation: {
      brand: "ODTÜ YEMEKHANE",
      home: "ANA SAYFA",
      admin: "YÖNETİCİ",
      service: "KART OKUYUCU",
      logout: "ÇIKIŞ",
      welcome: "Hoş geldin {name} ({role})",
      user: "Kullanıcı",
    },
    home: {
      title: "ODTÜ Yemekhane Yönetim Sistemi",
      subtitle:
        "Mevcut krediye göre sınırsız yemek erişimi sunan modern ön ödemeli yemekhane yönetimi",
      adminPanelTitle: "Yönetim Paneli",
      adminPanelDescription: "Öğrenci hesaplarını yönetin, bakiye ekleyin ve sistemi izleyin",
      adminPanelCta: "Yönetim Panelini Aç",
      serviceTitle: "Kart Okuyucu Arayüzü",
      serviceDescription:
        "Yemekhane personeli için hızlı kart tarama ve ödeme alma ekranı",
      serviceCta: "Kart Okuyucuyu Aç",
      featuresTitle: "Yeni Sistem Özellikleri",
      features: {
        prepaidTitle: "Ön Ödemeli Bakiye Sistemi",
        prepaidDescription: "Öğrenciler kartlarına kredi yükler ve ihtiyaç kadar harcar",
        flexibleTitle: "Esnek Yemek Seçenekleri",
        flexibleDescription: "Tabldot (100 TL), Ala Carte (150 TL) veya özel fiyatlandırma",
        unlimitedTitle: "Sınırsız Kullanım",
        unlimitedDescription: "Günlük limit yok - bakiyeniz elverdiği sürece yiyin",
      },
    },
    auth: {
      loginTitle: "ODTÜ Yemekhane Yönetimi",
      loginSubtitle: "Yönetici Girişi Gereklidir",
      username: "Kullanıcı Adı",
      password: "Şifre",
      usernamePlaceholder: "Kullanıcı adınızı girin",
      passwordPlaceholder: "Şifrenizi girin",
      signIn: "Giriş Yap",
      signingIn: "Giriş yapılıyor...",
      defaultCredentials: "Varsayılan Giriş Bilgileri:",
      defaultUsername: "Kullanıcı Adı",
      defaultPassword: "Şifre",
      defaultWarning: "İlk girişten sonra lütfen varsayılan şifreyi değiştirin.",
      errors: {
        requiredFields: "Lütfen tüm alanları doldurun",
        loginFailed: "Giriş başarısız. Bilgilerinizi kontrol edin.",
        welcomeBack: "Tekrar hoş geldin, {username}!",
      },
    },
    adminPanel: {
      title: "Yönetim Paneli",
      description: "Öğrenci hesaplarını yönetin ve ön ödemeli bakiyeleri takip edin",
      loading: "Öğrenciler yükleniyor...",
      addStudent: "Yeni Öğrenci Ekle",
      editStudent: "Öğrenciyi Düzenle",
      addAdmin: "Yönetici/Kasiyer Ekle",
      cleanup: "Süresi Dolanları Temizle (100+ gün)",
      studentsCard: {
        title: "Öğrenci Yönetimi",
        subtitle: "Öğrenci aboneliklerini oluşturun, güncelleyin ve yönetin",
      },
      status: {
        active: "Aktif",
        expired: "Süresi Dolmuş",
        inactive: "Pasif",
        expiringSoon: "Yakında Dolacak",
        noBalance: "Bakiye Yok",
      },
      mealTypes: {
        table_dhote: "Tabldot",
        alacarte: "Ala Carte",
        custom: "Özel",
        unknown: "Bilinmiyor",
      },
      stats: {
        totalStudents: "Toplam Öğrenci",
        activeStudents: "Aktif",
        expiringSoon: "Yakında Dolacak",
        expired: "Süresi Dolmuş",
        transactionsToday: "Bugünkü İşlem",
        totalBalance: "Toplam Bakiye",
        revenue: "Gelir",
      },
      form: {
        name: "Adı",
        surname: "Soyadı",
        schoolId: "Okul No",
        cardId: "Kart No",
        cardIdReadOnly: "Kart No (Salt Okunur)",
        mealType: "Yemek Tipi",
        duration: "Süre",
        mealsPerDay: "Günlük Yemek",
        customBalance: "Özel Bakiye (TL)",
        calculate: "Bakiye Hesaplama",
        submit: "Öğrenci Ekle",
        update: "Öğrenciyi Güncelle",
        cancel: "İptal",
        scanPrompt: "1. Adım: Kart Numarasını Oku *",
        scanning: "Kontrol ediliyor...",
        lookup: "Kartı Kontrol Et",
        lookupPlaceholder: "Kartı okutun veya numarayı yazın (Enter)",
        scanHelp: "Kartı okutun ya da yazıp Enter'a basarak bilgileri doldurun.",
        balance: "Bakiye (TL)",
      },
      placeholders: {
        name: "Öğrencinin adını girin",
        surname: "Öğrencinin soyadını girin",
        schoolId: "Okul numarasını girin",
        customBalance: "Özel bakiye tutarını girin",
      },
      mealOptions: {
        tableDhote: "Tabldot (3000 TL taban)",
        alacarte: "Ala Carte (4500 TL taban)",
        custom: "Özel Tutar",
      },
      durationOptions: {
        30: "30 Gün",
        60: "60 Gün",
        90: "90 Gün",
      },
      mealsPerDayOptions: {
        one: "Günde 1 Yemek",
        two: "Günde 2 Yemek",
      },
      balance: {
        custom: "Özel Bakiye: {amount}",
        base: "Taban Ücret:",
        duration: "Süre:",
        days: "gün",
        mealsPerDay: "Günlük yemek:",
        total: "Toplam Bakiye: {amount}",
      },
      table: {
        name: "Adı Soyadı",
        cardId: "Kart No",
        schoolId: "Okul No",
        balance: "Bakiye",
        expires: "Bitiş Tarihi",
        status: "Durum",
        actions: "İşlemler",
        noStudents: "Öğrenci bulunamadı",
        emptyHelper: "Başlamak için ilk öğrencinizi ekleyin",
      },
      notifications: {
        fetchStudentsError: "Öğrenciler alınamadı",
        newCard: "✨ Yeni kart algılandı - lütfen öğrenci bilgilerini doldurun",
        cardActive:
          "⚠️ Kartın AKTİF aboneliği var!\nÖğrenci: {name}\nMevcut bakiye: {balance}\nBitiş: {expires}\nDeğişiklik için Düzenle bölümünü kullanın.",
        cardExpired:
          "🔄 Kartın aboneliği SÜRESİ DOLMUŞ.\nÖğrenci: {name}\nÖnceki bakiye: {balance} (sıfırlanacak)\nBitiş: {expires}\nYeni abonelik için hazır!",
        cardInactive:
          "📋 Kart pasif abonelik içeriyor.\nÖğrenci: {name}\nÖnceki bakiye: {balance} (sıfırlanacak)\nYeni abonelik oluşturabilirsiniz!",
        lookupError: "Kart sorgulanamadı",
        addSuccess: "Öğrenci {name} {surname} eklendi! Bakiye: {balance}",
        addError: "Lütfen zorunlu alanları doldurun",
        addFailed: "Öğrenci eklenemedi",
        updateSuccess: "Öğrenci güncellendi! Yeni bakiye: {balance}",
        updateError: "Öğrenci güncellenemedi",
        deleteSuccess: "Öğrenci {name} silindi",
        deleteError: "Öğrenci silinemedi",
        adminAddSuccess: "Yönetici {username} oluşturuldu",
        adminAddError: "Lütfen kullanıcı adı ve şifre girin",
        adminAddFailed: "Yönetici eklenemedi",
        cleanupSuccess: "Süresi dolan abonelikler temizlendi",
        cleanupError: "Süresi dolan abonelikler temizlenemedi",
      },
      deleteConfirm: {
        title: "Öğrenciyi Sil",
        description: "{name} öğrencisini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
        confirm: "Sil",
      },
      adminForm: {
        title: "Yönetici veya Kasiyer Oluştur",
        username: "Kullanıcı Adı",
        usernamePlaceholder: "Kullanıcı adını girin",
        password: "Şifre",
        passwordPlaceholder: "Şifrenizi girin",
        role: "Rol",
        submit: "Kullanıcı Ekle",
      },
      roles: {
        admin: "Yönetici (Tam Erişim)",
        cashier: "Kasiyer (Sadece Kart Okuyucu)",
        help: "Yönetici: tüm özelliklere erişir. Kasiyer: yalnızca kart okuyucu kullanır.",
      },
      editHelp: "Buradan öğrencinin bakiyesini düzenleyebilirsiniz. Fon eklemek veya düzeltme yapmak için kullanın.",
    },
    service: {
      title: "Kart Okuyucu Arayüzü",
      subtitle: "Bakiye kontrolü ve yemek ödemesi için öğrenci kartını okutun",
      lastScanned: "Son okuma: {time}",
      cardScannerLabel: "Kart Numarası Okuyucu",
      cardPlaceholder: "Kartı okutun veya kart numarasını yazın...",
      checkCard: "Kartı Kontrol Et",
      checking: "Kontrol ediliyor...",
      clear: "Temizle",
      cardFound: "Kart bulundu: {student}",
      systemError: "Sistem hatası - lütfen tekrar deneyin",
      deductionError: "Yemek bakiyesi düşülemedi",
      scanPrompt: "Lütfen önce geçerli bir kart okutun",
      customAmountPrompt: "Lütfen geçerli bir tutar girin",
      cardStatus: {
        found: "KART BULUNDU",
        issue: "KART SORUNU",
        message: "{message}",
      },
      balance: "Bakiye: {amount}",
      expires: "Bitiş: {date}",
      expiresSoon: "Yakında Dolacak",
      selectMeal: "Yemek Tipini Seç",
      meals: {
        table_dhote: "Tabldot",
        alacarte: "Ala Carte",
        custom: "Özel Tutar",
        customLabel: "₺ Özel",
      },
      customPlaceholder: "Özel tutar girin (TL)",
      processing: "İşleniyor...",
      deduct: "{amount} düş",
      paymentResult: "Ödeme Sonucu",
      paymentSuccessful: "ÖDEME BAŞARILI",
      paymentFailed: "ÖDEME BAŞARISIZ",
      nextCustomer: "Sıradaki Müşteri",
      tryAgain: "Tekrar Dene",
      paymentDetails: {
        student: "Öğrenci:",
        amountDeducted: "Düşülen Tutar:",
        previousBalance: "Önceki Bakiye:",
        newBalance: "Yeni Bakiye:",
      },
      instructionsTitle: "Talimatlar",
      instructions: [
        "• Öğrencinin RFID kartını okutun veya kart numarasını manuel girin",
        "• Sistem öğrencinin bakiyesini ve abonelik durumunu kontrol eder",
        "• Yemek tipi seçin: Tabldot (100 TL), Ala Carte (150 TL) veya özel tutar",
        "• \"Düş\" butonuna basarak ödemeyi gerçekleştirin",
        "• Günlük limit yoktur - bakiye olduğu sürece öğrenciler yemek yiyebilir",
        "• Yeşil = Başarılı ödeme, Kırmızı = Başarısız ödeme veya yetersiz bakiye",
        "• Abonelikler 100+ gün işlem yapılmadığında otomatik biter",
      ],
      quickStats: {
        success: "Başarılı Ödeme",
        failed: "Başarısız Ödeme",
        table: "Tabldot",
        alacarte: "Ala Carte",
        unlimited: "Günlük Limit Yok",
      },
    },
  },
};

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  t: (key, variables) => {
    const keys = key.split(".");
    let value = translations.en;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        return key;
      }
    }

    if (typeof value === "string") {
      return value;
    }

    return value;
  },
});

const interpolate = (text, variables = {}) => {
  if (typeof text !== "string") return text;
  return text.replace(/\{(\w+)\}/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(variables, key)
      ? variables[key]
      : match;
  });
};

const getTranslation = (language, key) => {
  const keys = key.split(".");
  let value = translations[language];
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) {
      return undefined;
    }
  }
  return value;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("language") || "en";
    }
    return "en";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("language", language);
    }
  }, [language]);

  const value = useMemo(() => {
    const t = (key, variables = {}) => {
      let translation = getTranslation(language, key);
      if (translation === undefined) {
        translation = getTranslation("en", key) ?? key;
      }
      if (typeof translation === "string") {
        return interpolate(translation, variables);
      }
      if (Array.isArray(translation)) {
        return translation.map((item) =>
          typeof item === "string" ? interpolate(item, variables) : item
        );
      }
      return translation;
    };

    return {
      language,
      setLanguage,
      t,
      translations,
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useTranslation must be used within a LanguageProvider");
  }
  return context;
};

export { translations };
